import { useState } from 'react';
import { useSelector } from 'react-redux';
import { DocumentType } from '~/constants/fileUpload';
import { CountryCode } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { IFile } from '~/interfaces/shared';
import { getSignedUrl, postFile } from '~/services/api';
import { RootState } from '~/store';
import { ApiStatus } from '~/store/types/sharedTypes';

const createFilesFromObjectUrls = (documents: IFile[]) =>
  Promise.all(
    documents.map(async (document) => {
      const res = await fetch(document.objectURL);
      const blob = await res.blob();
      return new File([blob], document.name, blob);
    })
  );

const postFilesToS3 = async (
  files: File[],
  fileKeys: string[],
  clientId: string,
  market: CountryCode,
  documentType: DocumentType
): Promise<string[]> => {
  const res = await getSignedUrl(fileKeys, clientId, market, documentType);
  await Promise.all(
    res.data.map(async (infoContainer: any, i: number) => {
      const method = infoContainer.method;
      if (method === 'POST') {
        const form = new FormData();
        Object.keys(infoContainer.fields).forEach((key) =>
          form.append(key, infoContainer.fields[key])
        );
        form.append('file', files[i]);
        await postFile(infoContainer.url, form, infoContainer.method);
      } else if (method === 'PUT') {
        await postFile(infoContainer.url, files[i], infoContainer.method);
      } else {
        throw new Error('Unsupported file upload method');
      }
    })
  );
  return res.data.map((file: any) => file.fields.key);
};

interface UseDocumentUploadProps {
  documentType: DocumentType;
}

export const useDocumentUpload = ({ documentType }: UseDocumentUploadProps) => {
  const market = useSelector((state: RootState) => state.intl.market);
  const { clientId } = useSelector((state: RootState) => state.loanApplication);

  const [documentsApiStatus, setDocumentsApiStatus] = useState<ApiStatus>(
    ApiStatus.Idle
  );

  const uploadDocuments = async (files: IFile[]): Promise<string[] | null> => {
    setDocumentsApiStatus(ApiStatus.Started);
    try {
      const resolvedFiles = await createFilesFromObjectUrls(files);
      const fileKeys = files.map((f) => f.name);
      const filePaths = await postFilesToS3(
        resolvedFiles,
        fileKeys,
        clientId ? String(clientId) : '',
        market,
        documentType
      );
      setDocumentsApiStatus(ApiStatus.Completed);
      return filePaths;
    } catch (err: any) {
      setDocumentsApiStatus(ApiStatus.Failed);
      logToSentry(err, 'uploadDocuments');
      return null;
    }
  };

  return { documentsApiStatus, uploadDocuments };
};
